import { useEffect, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
  Marker,
} from "react-simple-maps";
import { CircularProgress } from "@mui/material";

import MapTooltip, { Tooltip } from "../mapTooltip";
import CardContainer from "../cardContainer";
import { Device } from "../../api/types/devices";

import geoData from "../../assets/merged_world.json";
import UnitData from "../_modals/UnitData";
import classes from "./index.module.scss";

type Props = {
  dataPoints: Device[];
  isLoading: boolean;
};

type Coordinate = [number, number];
type MarkerProps = {
  device_id: string;
  sensorName: string;
  lastReading: number;
  status: string;
  coordinates: Coordinate;
};

const Map = (props: Props) => {
  const { dataPoints, isLoading } = props;

  const [markers, setMarkers] = useState<MarkerProps[]>([]);
  const [tooltipData, setTooltipData] = useState<Tooltip | null>(null);
  const [unitDataOpen, setUnitDataOpen] = useState<boolean>(false);
  const [selectedUnit, setSelectedUnit] = useState<{uid: string}>();

  useEffect(() => {
    const newMarkers: any = [];
    for (let dp of dataPoints) {
      newMarkers.push({
        device_id: dp.device_id,
        sensorName: dp.sensor_name,
        lastReading: dp.data,
        status: dp.status,
        coordinates: [
          parseFloat(dp.lon as string),
          parseFloat(dp.lat as string),
        ],
      });
    }

    setMarkers(newMarkers);
  }, [dataPoints]);

  const getMarkerColor = (value: number): string => {
    if (value <= 15) return "#76A355";
    else if (value <= 30) return "#959595";
    else if (value <= 45) return "#ffbf00";
    else return "#ff4000";
  };

  const getMarkerStroke = (status: string): string => {
    if (status === "on") return "#0ead69";
    else if (status === "off") return "#e5383b";
    else return "#FFB703";
  };

  const handleMarkerClick = (uid: string) => {
    setUnitDataOpen(true);
    setSelectedUnit({uid});
  };

  return (
    <>
      <CardContainer
        style={{ width: "62.5%", aspectRatio: "1.3/1", display: "flex", justifyContent: "center" }}
      >
        {isLoading ? (
          <div className={classes.loaderContainer}>
            <CircularProgress style={{ alignSelf: "center", margin: "auto" }} />
            <div className={classes.loaderText}>Loading the latest data. Please be patient</div>
          </div>
        ) : (
          <ComposableMap
            style={{ margin: "20px 10px", borderRadius: 5 }}
            projectionConfig={{
              scale: 2500,
            }}
          >
            <ZoomableGroup zoom={1.5} center={[-3.5, 55]}>
              <Geographies geography={geoData}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      geography={geo}
                      fill="rgba(255, 255, 255, 0.514)"
                      stroke="#FFF"
                      strokeWidth={0.2}
                      style={{
                        default: { outline: "none" },
                        hover: { outline: "none" },
                        pressed: { outline: "none" },
                      }}
                    />
                  ))
                }
              </Geographies>
              {markers.map(
                (
                  { device_id, sensorName, lastReading, status, coordinates },
                  index
                ) => (
                  <Marker
                    data-tip=""
                    key={`${device_id}-${coordinates.toString()}-${index}`}
                    coordinates={coordinates}
                    onClick={() => handleMarkerClick(device_id)}
                    onMouseEnter={() => {
                      setTooltipData({
                        lastReading,
                        sensorName,
                        coordinates,
                      });
                    }}
                    onMouseLeave={() => {
                      setTooltipData(null);
                    }}
                    style={{
                      default: { outline: "none" },
                      hover: { outline: "none" },
                      pressed: { outline: "none" },
                    }}
                  >
                    <circle
                      r={1.7}
                      fill={getMarkerColor(lastReading)}
                      stroke={getMarkerStroke(status)}
                      strokeWidth={0.3}
                    />
                  </Marker>
                )
              )}
            </ZoomableGroup>
          </ComposableMap>
        )}
      </CardContainer>
      <MapTooltip {...{ tooltipData }} />
      {selectedUnit && (
        <UnitData
          open={unitDataOpen}
          onClose={() => setUnitDataOpen(false)}
          unit={selectedUnit}
        />
      )}
    </>
  );
};

export default Map;
